<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      left-side-class="col-xl-6 pb-2"
      :page="headerConfig"
      right-side-class="col-xl-6 align-items-end d-flex pb-4 justify-content-end"
    />

    <section-title content="Información General" has-line />

    <div class="row">
      <div class="col-md-4">
        <div class="color-secondary-6 font-14">
          Año Académico:
        </div>
        <div class="color-black font-16">
          {{ detail.academicYear }}
        </div>
      </div>
      <div class="col-md-4">
        <div class="color-secondary-6 font-14">
          Área:
        </div>
        <div class="color-black font-16">
          {{ detail.area }}
        </div>
      </div>
      <div class="col-md-4">
        <div class="color-secondary-6 font-14 mr-4">
          Tipo de Proveedor:
        </div>
        <div class="color-black font-16">
          {{ detail.recruitmentName }}
        </div>
      </div>
    </div>

    <section-title class="mt-3" content="Listado de Documentos" has-line />

    <div class="d-flex justify-content-end mb-3">
      <action-button
        v-if="canEditDocuments && !this.addingDocument"
        class="mr-2"
        :class="
          isDragAndDropEnable
            ? 'custom-reorganize-btn text-white'
            : 'outline-dark-gray'
        "
        i="far fa-sort-numeric-up"
        :text="
          isDragAndDropEnable
            ? 'Finalizar Reorganización'
            : 'Reorganizar Documentos'
        "
        @click="reorganize()"
      />
      <action-button
        v-if="
          canEditDocuments && !this.addingDocument && !this.isDragAndDropEnable
        "
        i="fas fa-file-plus"
        text="Añadir Documento"
        variant="success"
        @click="() => (this.addingDocument = true)"
      />
    </div>
    <add-document
      v-if="addingDocument"
      class="mb-3"
      :document-types="documentTypes"
      @on-cancel="() => (this.addingDocument = false)"
      @on-create="onAddDocumentHandler($event)"
    />

    <draggable
      v-model="detail.documents"
      :disabled="!isDragAndDropEnable"
      force-fallback
      ghost-class="ghost"
    >
      <document-row
        v-for="item in detail.documents"
        :key="item.proposalDocumentTypeId"
        :draggable-enabled="isDragAndDropEnable"
        :value="item"
        @on-delete="onDeleteDocumentHandler($event)"
        @on-update="onUpdateDocumentHandler($event)"
      />
    </draggable>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import draggable from 'vuedraggable';

import {
  getDetail as _getDetail,
  deleteDocument as _deleteDocument,
  updateDocument as _updateDocument,
  getDocumentTypes as _getDocumentTypes,
  addDocument as _addDocument,
  reorderDocuments as _reorderDocuments,
} from '@/modules/proposalDocumentConfiguration/api.js';

import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import DocumentRow from './components/DocumentRow';
import AddDocument from './components/AddDocument';

export default {
  name: 'DocumentConfigurationDetail',
  components: {
    PageHeader,
    SectionTitle,
    ActionButton,
    DocumentRow,
    AddDocument,
    draggable,
  },
  mixins: [alertMixin, storeMixin],
  data() {
    return {
      configurationId: null,
      addingDocument: false,
      documentTypes: [],
      detail: {},
      isDragAndDropEnable: false,
    };
  },
  computed: {
    headerConfig() {
      return {
        title: 'Configuración Documentos para Pliegos',
        icon: 'fas fa-file-invoice',
        routes: [
          'Proceso de Competencia',
          'Configuración Documentos para Pliegos',
        ],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({ name: 'DocumentConfiguration' });
          },
        },
      ];
    },
    canEditDocuments() {
      return this.userHasPermissions('provider.Edit.Configuration.Documents');
    },
  },
  async mounted() {
    await this.getDetail();
    await this.getDocumentTypes();
  },
  methods: {
    async getDetail() {
      const { id } = this.$route.params;
      this.configurationId = Number(id);
      const { data } = await _getDetail(id);
      this.detail = data;
    },
    async getDocumentTypes() {
      const { data } = await _getDocumentTypes(this.configurationId);
      this.documentTypes = data;
    },
    async onDeleteDocumentHandler(proposalDocumentTypeId) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro desea eliminar documento de la configuración?',
        'Si',
        'No'
      );

      if (!confirmed) return;

      await _deleteDocument(proposalDocumentTypeId);

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido eliminado exitosamente.',
        'success'
      );

      await this.getDetail();
      await this.getDocumentTypes();
    },
    async onUpdateDocumentHandler(payload) {
      await _updateDocument(payload);

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido actualizado exitosamente.',
        'success'
      );

      await this.getDetail();
    },
    async onAddDocumentHandler(payload) {
      payload = {
        documentConfigurationId: this.configurationId,
        ...payload,
      };

      await _addDocument(payload);

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido añadido exitosamente.',
        'success'
      );

      this.addingDocument = false;
      await this.getDocumentTypes();
      await this.getDetail();
    },
    async reorganize() {
      if (this.isDragAndDropEnable) {
        let payload = {
          configurationDocumentId: this.configurationId,
          documents: this.detail.documents,
        };

        await _reorderDocuments(payload);

        this.ShowToast(
          '',
          'Los documentos fueron reorganizados exitosamente.',
          'success'
        );
      }

      this.isDragAndDropEnable = !this.isDragAndDropEnable;
    },
  },
};
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.custom-reorganize-btn {
  background-color: #405c64;
}

.outline-dark-gray {
  color: #3f5b60;
  background-color: #ffffff;
  border-color: #3f5b60;
}
</style>
